'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/navbar';
import { Footer } from '@/components/footer';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'detailing',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submission
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', phone: '', service: 'detailing', message: '' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <>
      <Navbar data-zylo-id="zylo-e15f56863fd7" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="54:7-54:17" data-zylo-component="ContactPage" />

      {/* Hero */}
      <section data-zylo-id="zylo-14ecd8255a50" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="57:7-79:17" data-zylo-component="ContactPage" className="bg-gradient-to-b from-accent/10 via-white to-white pt-20 pb-16 sm:pt-32 sm:pb-24">
        <div data-zylo-id="zylo-71fe7072ea09" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="58:9-78:15" data-zylo-component="ContactPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-516f5a2acd2a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="59:11-77:24" data-zylo-component="ContactPage"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-3xl text-center"
          >
            <motion.h1 data-zylo-id="zylo-343b7c9cd00f" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="65:13-70:25" data-zylo-component="ContactPage"
              variants={itemVariants}
              className="text-4xl font-bold sm:text-5xl text-foreground mb-6"
            >
              Get in Touch
            </motion.h1>
            <motion.p data-zylo-id="zylo-aa78237a834b" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="71:13-76:24" data-zylo-component="ContactPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground"
            >
              Have questions? We'd love to hear from you. Contact us anytime!
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section data-zylo-id="zylo-76966fbbac3e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="82:7-202:17" data-zylo-component="ContactPage" className="py-16 sm:py-24">
        <div data-zylo-id="zylo-22ad84e78bf9" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="83:9-201:15" data-zylo-component="ContactPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-3f7887537cc1" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="84:11-125:24" data-zylo-component="ContactPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          >
            {[
              {
                icon: Phone,
                title: 'Phone',
                content: '(123) 456-7890',
                subtitle: 'Available Mon-Sat, 8am-6pm'
              },
              {
                icon: Mail,
                title: 'Email',
                content: 'info@bluekraken.com',
                subtitle: 'We respond within 24 hours'
              },
              {
                icon: MapPin,
                title: 'Address',
                content: '123 Ocean Drive',
                subtitle: 'Coastal City, CC 12345'
              }
            ].map((contact, index) => {
              const Icon = contact.icon;
              return (
                <motion.div data-zylo-id="zylo-2839936c3fdb" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="113:17-122:30" data-zylo-component="ContactPage" key={index} variants={itemVariants}>
                  <Card data-zylo-id="zylo-aefc8c3032cc" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="114:19-121:26" data-zylo-component="ContactPage" className="border-0 shadow-lg h-full">
                    <CardContent data-zylo-id="zylo-e81d06dc4c96" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="115:21-120:35" data-zylo-component="ContactPage" className="p-6 text-center">
                      <Icon data-zylo-id="zylo-f64e4ce6ffb6" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="116:23-116:78" data-zylo-component="ContactPage" className="w-12 h-12 text-accent mx-auto mb-4" />
                      <h3 data-zylo-id="zylo-33befe90796b" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="117:23-117:82" data-zylo-component="ContactPage" className="text-xl font-bold mb-2">{contact.title}</h3>
                      <p data-zylo-id="zylo-2fd5b968c8a8" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="118:23-118:89" data-zylo-component="ContactPage" className="font-semibold text-foreground mb-1">657-200-1531</p>
                      <p data-zylo-id="zylo-a29957f03908" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="119:23-119:90" data-zylo-component="ContactPage" className="text-sm text-muted-foreground">{contact.subtitle}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Contact Form */}
          <motion.div data-zylo-id="zylo-beb620a0c00c" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="128:11-200:24" data-zylo-component="ContactPage"
            variants={itemVariants}
            className="max-w-2xl mx-auto"
          >
            <Card data-zylo-id="zylo-ce07d100bb3c" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="132:13-199:20" data-zylo-component="ContactPage" className="border-0 shadow-lg p-8">
              <h2 data-zylo-id="zylo-e79a71452048" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="133:15-133:93" data-zylo-component="ContactPage" className="text-2xl font-bold text-foreground mb-6">Send us a Message</h2>
              <form data-zylo-id="zylo-2b2d8b2e0f5f" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="134:15-198:22" data-zylo-component="ContactPage" onSubmit={handleSubmit} className="space-y-4">
                <div data-zylo-id="zylo-d94a3135a72e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="135:17-146:23" data-zylo-component="ContactPage">
                  <label data-zylo-id="zylo-b6d94e61fb33" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="136:19-136:85" data-zylo-component="ContactPage" className="block text-sm font-semibold mb-2">Name *</label>
                  <input data-zylo-id="zylo-cd2ce6889d4e" data-zylo-kind="input" data-zylo-cap="63" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="137:19-145:21" data-zylo-component="ContactPage"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Your Name"
                  />
                </div>
                <div data-zylo-id="zylo-4c974fe18370" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="147:17-158:23" data-zylo-component="ContactPage">
                  <label data-zylo-id="zylo-e0fa09852791" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="148:19-148:86" data-zylo-component="ContactPage" className="block text-sm font-semibold mb-2">Email *</label>
                  <input data-zylo-id="zylo-56dcfbe2180d" data-zylo-kind="input" data-zylo-cap="63" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="149:19-157:21" data-zylo-component="ContactPage"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="your@email.com"
                  />
                </div>
                <div data-zylo-id="zylo-81af8b931086" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="159:17-169:23" data-zylo-component="ContactPage">
                  <label data-zylo-id="zylo-4e68d9d3324c" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="160:19-160:84" data-zylo-component="ContactPage" className="block text-sm font-semibold mb-2">Phone</label>
                  <input data-zylo-id="zylo-3a7527dc2f43" data-zylo-kind="input" data-zylo-cap="63" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="161:19-168:21" data-zylo-component="ContactPage"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div data-zylo-id="zylo-c636f89e2b9a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="170:17-182:23" data-zylo-component="ContactPage">
                  <label data-zylo-id="zylo-f995f7a32e42" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="171:19-171:100" data-zylo-component="ContactPage" className="block text-sm font-semibold mb-2">Service Interested In</label>
                  <select data-zylo-id="zylo-4e500373da97" data-zylo-kind="input" data-zylo-cap="63" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="172:19-181:28" data-zylo-component="ContactPage"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option data-zylo-id="zylo-a720e8b66149" data-zylo-kind="input" data-zylo-cap="63" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="178:21-178:70" data-zylo-component="ContactPage" value="detailing">Auto Detailing</option>
                    <option data-zylo-id="zylo-a2fe7003f9e5" data-zylo-kind="input" data-zylo-cap="63" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="179:21-179:74" data-zylo-component="ContactPage" value="inspections">Home Inspections</option>
                    <option data-zylo-id="zylo-6b2f6974eb82" data-zylo-kind="input" data-zylo-cap="63" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="180:21-180:64" data-zylo-component="ContactPage" value="both">Both Services</option>
                  </select>
                </div>
                <div data-zylo-id="zylo-f1a29656732f" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="183:17-194:23" data-zylo-component="ContactPage">
                  <label data-zylo-id="zylo-aca39e7e3200" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="184:19-184:88" data-zylo-component="ContactPage" className="block text-sm font-semibold mb-2">Message *</label>
                  <textarea data-zylo-id="zylo-daf704357fd0" data-zylo-kind="input" data-zylo-cap="63" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="185:19-193:21" data-zylo-component="ContactPage"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Your message..."
                  />
                </div>
                <Button data-zylo-id="zylo-3297af5f2702" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="195:17-197:26" data-zylo-component="ContactPage" type="submit" className="w-full bg-accent hover:bg-accent/90">
                  Send Message
                </Button>
              </form>
            </Card>
          </motion.div>
        </div>
      </section>

      <Footer data-zylo-id="zylo-eea39fe844d8" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/contact/page.tsx" data-zylo-loc="204:7-204:17" data-zylo-component="ContactPage" />
    </>
  );
}
